import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ProtectedRoute - Wrapper for authenticated routes
 * Checks session via /api/auth/me endpoint using localStorage token
 */
export function ProtectedRoute({ children }) {
  const [authState, setAuthState] = useState({
    isAuthenticated: null, // null = checking, true = authenticated, false = not authenticated
    user: null
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setAuthState({
        isAuthenticated: true,
        user: location.state.user
      });
      return;
    }

    // Check authentication via server
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) {
          setAuthState({ isAuthenticated: false, user: null });
          navigate('/login', { replace: true });
          return;
        }

        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        
        if (data.success && data.user) {
          setAuthState({
            isAuthenticated: true,
            user: data.user
          });
        } else {
          localStorage.removeItem('session_token');
          setAuthState({
            isAuthenticated: false,
            user: null
          });
          navigate('/login', { replace: true });
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        setAuthState({
          isAuthenticated: false,
          user: null
        });
        navigate('/login', { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  // Loading state
  if (authState.isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - will redirect in useEffect
  if (!authState.isAuthenticated) {
    return null;
  }

  // Authenticated - render children with user context
  return React.cloneElement(children, { user: authState.user });
}

export default ProtectedRoute;
